import "animate.css/animate.min.css";
import cn from "classnames";
import PropTypes from "prop-types";
import { useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { ReactReduxContext } from "react-redux";
import { useSwipeable } from "react-swipeable";

import { Portal } from "shared/common-ui/Portal";

import { useResizeObserver } from "shared/hooks/useResizeObserver.ts";

import { selectIsTopModal } from "shared/redux/selectors/modalStack";
import { registerModal, unregisterModal } from "shared/redux/slices/modalStack";

import styles from "@platformAssets/components/Modal.module.scss";

import { ReactComponent as CloseIcon } from "assets/shared/img/icons/cross.svg";

const MIN_TOP_OFFSET = 24;

let openModalCount = 0;
let originalBodyOverflow = "";

export const Modal = ({
  bodyClass,
  bodyRef = null,
  children,
  close,
  closeModalOnClickOutside = true,
  customClass,
  customId,
  hideClose = null,
  swipedDown = false,
  isAnimated = false,
  withPortal = false,
}) => {
  const id = useId();
  const [fixedToTop, setFixedToTop] = useState(false);
  const modal = useRef(null);
  const [isTopInStack, setIsTopInStack] = useState(true);

  // Modal can work with Redux and without it
  const reduxContext = useContext(ReactReduxContext);
  const store = reduxContext?.store ?? null;

  // Create selector once for current modal id
  const isTopModalSelector = useMemo(() => selectIsTopModal(id), [id]);

  //Handlers for swipe events
  const handlers = useSwipeable({
    onSwipedDown: (event) => (swipedDown ? close(event) : null),
  });

  useEffect(() => {
    // Register modal only when Redux store exists
    if (!store) {
      return;
    }

    store.dispatch(registerModal(id));

    return () => {
      store.dispatch(unregisterModal(id));
    };
  }, [store, id]);

  useEffect(() => {
    // Without Redux, this modal is always top
    if (!store) {
      setIsTopInStack(true);
      return;
    }

    const updateIsTopModal = () => {
      const state = store.getState();
      setIsTopInStack(isTopModalSelector(state));
    };

    updateIsTopModal();

    const unsubscribe = store.subscribe(updateIsTopModal);

    return () => {
      unsubscribe();
    };
  }, [store, isTopModalSelector]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isTopInStack) {
        close();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isTopInStack]);

  //To prevent scroll content, when modal is open
  useEffect(() => {
    if (openModalCount === 0) {
      originalBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    openModalCount += 1;

    const handleOrientationChange = () => {
      setTimeout(setFixPosition, 500);
    };

    setFixPosition();

    window.addEventListener("resize", setFixPosition);
    window.addEventListener("orientationchange", handleOrientationChange);

    return () => {
      openModalCount -= 1;
      if (openModalCount === 0) {
        document.body.style.overflow = originalBodyOverflow;
      }
      window.removeEventListener("resize", setFixPosition);
      window.removeEventListener("orientationchange", handleOrientationChange);
    };
  }, []);

  useEffect(() => {
    if (bodyRef && modal.current) {
      bodyRef.current = modal.current;
    }
  }, [bodyRef, modal.current]);

  /**
   * Get modal position to top of window
   * @returns {null|number}
   */
  const getTopOffset = () => {
    if (!modal?.current) {
      return null;
    }
    return window.innerHeight - modal.current.clientHeight;
  };

  /**
   * Set fixed or absolute position of modal window
   */
  const setFixPosition = () => {
    const topOffset = getTopOffset();

    const shouldFixToTop = topOffset !== null && topOffset <= MIN_TOP_OFFSET;

    setFixedToTop(shouldFixToTop);
  };

  useResizeObserver(modal, setFixPosition);

  const handleClickOutside = (e) => {
    e.preventDefault();

    if (closeModalOnClickOutside) {
      return close();
    }

    return null;
  };

  const modalContent = (
    <div
      id={customId}
      className={cn(
        styles.ModalWrap,
        {
          [styles.ModalWrapFixed]: fixedToTop,
        },
        customClass
      )}
      {...handlers}
    >
      <div
        className={cn(styles.ModalFade, {
          [styles.AnimatedFade]: isAnimated,
        })}
        onClick={handleClickOutside}
      />
      <div
        className={cn(
          styles.ModalBody,
          {
            [styles.AnimatedBody]: isAnimated,
          },
          bodyClass
        )}
        ref={modal}
      >
        {!hideClose && (
          <div className={styles.ModalClose} onClick={close}>
            <CloseIcon />
          </div>
        )}
        {children}
      </div>
    </div>
  );

  return withPortal ? <Portal>{modalContent}</Portal> : modalContent;
};

Modal.propTypes = {
  customId: PropTypes.string,
  hideClose: PropTypes.bool,
  close: PropTypes.func,
  customClass: PropTypes.string,
  swipedDown: PropTypes.bool,
  children: PropTypes.node.isRequired,
  isAnimated: PropTypes.bool,
  withPortal: PropTypes.bool,
  bodyClass: PropTypes.string,
};

export function ModalFooter({ customClass, children }) {
  return <div className={cn(styles.ModalFooter, customClass)}>{children}</div>;
}

ModalFooter.propTypes = {
  customClass: PropTypes.string,
  children: PropTypes.node.isRequired,
};

export function ModalText({ text, isTitle, customClass }) {
  const className = cn(
    styles.ModalText,
    {
      [styles.ModalTitle]: isTitle,
    },
    styles[customClass] || customClass
  );

  if (typeof text !== "string") {
    return <p className={className}>{text}</p>;
  }

  return <p className={className} dangerouslySetInnerHTML={{ __html: text }} />;
}

ModalText.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  isTitle: PropTypes.bool,
  customClass: PropTypes.string,
};

export function ModalImage({ src, isCircle, customClass }) {
  // If `customClass` is an array, map over it and get corresponding styles or use the class name directly.
  const customClasses = Array.isArray(customClass)
    ? customClass.map((cls) => (styles[cls] ? styles[cls] : cls)).join(" ")
    : styles[customClass] || customClass;

  return (
    <div
      className={cn(styles.ModalImage, customClasses, {
        [styles.Circle]: isCircle,
      })}
    >
      <img src={src} alt="" />
    </div>
  );
}

ModalImage.propTypes = {
  src: PropTypes.string.isRequired,
  isCircle: PropTypes.bool,
  customClass: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
};

export function ModalError({ text, visible, customClass }) {
  return (
    <div
      className={
        styles.ModalError +
        (customClass ? ` ${styles[customClass]}` : "") +
        (!visible ? ` ${styles.hiddenErrorModal}` : "")
      }
    >
      {text}
    </div>
  );
}

ModalError.propTypes = {
  text: PropTypes.string.isRequired,
  visible: PropTypes.bool,
  customClass: PropTypes.string,
};
